'use client';
import accountStyles from './AccountSurface.module.css';
import { LiquidGlass } from '@/components/ui/liquid-glass/LiquidGlass';

import { localeDefinitions, selectLocale } from '@/i18n/locales';
import { SystemLoadingSurface } from '@/components/ui/SystemLoading';


import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import {
  CheckCircle2,
  ChevronRight,
  CreditCard,
  FileText,
  Gift,
  HelpCircle,
  KeyRound,
  LayoutDashboard,
  Link2,
  LogOut,
  RotateCcw,
  Share2,
  ShieldCheck,
  SlidersHorizontal,
  Upload,
  UserRound,
  WalletCards,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';

import { Avatar } from '@/components/common';
import { Button, Modal } from '@/components/ui';
import { routing, type Locale } from '@/i18n/routing';
import { cn } from '@/lib/cn';
import { POST_LOGOUT_PATH } from '@/lib/auth/login-route';
import { useCredits } from '@/lib/hooks/useCredits';
import { replaceCreditUnitLabel } from '@/lib/credits/unit';
import { pricingModal } from '@/lib/stores/pricing-modal-store';
import { creatorReferralsModal } from '@/lib/stores/creator-referrals-modal-store';
import { toast } from '@/lib/stores/toast-store';
import { useLargeDobiSpendConfirmEnabled } from '@/lib/stores/generation-confirm-preferences';
import { getUserFacingErrorMessage } from '@/lib/errors/user-facing-error';
import { useFeatureFlag } from '@/lib/stores/feature-flags-store';
import { subscriptionsApi, type CurrentSubscription } from '@/lib/api/subscriptions';
import { resolveCurrentSubscriptionPlanTitle } from '@/lib/subscription-title';
import {
  IMAGE_FILE_INPUT_ACCEPT,
} from '@/lib/api/materials';
import { userApi } from '@/lib/api/users';
import { useAuthStore, type AuthSecurityData } from '@/stores/authStore';
import { useUserProfileStore } from '@/stores/userProfileStore';
import { AvatarCropModal } from './AvatarCropModal';
import { OrdersInvoicesModal } from './OrdersInvoicesModal';
import { SocialAccountManager } from '@/features/social-publishing/SocialAccountManager';



interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ProfileSettingsSection = 'account' | 'general' | 'social' | 'billing' | 'security';

function SensitiveSkeleton({ className }: { className?: string }) {
  return (
    <SystemLoadingSurface as="span"
      aria-hidden="true"
      className={cn('inline-block  rounded-md ', className)}
    />
  );
}

export function ProfileModal({ isOpen, onClose }: ProfileModalProps) {
  const t = useTranslations('nav');
  const authT = useTranslations('auth');
  const commonT = useTranslations('common');
  const pricingT = useTranslations('pricing');
  const locale = useLocale();
  const router = useRouter();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { user, logout, updatePassword, fetchAuthSecurity } = useAuthStore();
  const { credits } = useCredits({ autoInit: isOpen });
  const unitText = useCallback((text: string) => replaceCreditUnitLabel(text, credits, locale), [credits, locale]);
  const showTopup = useFeatureFlag('payment_topup');
  const {
    profile,
    isLoading,
    isSaving,
    fetchProfile,
    updateProfile,
    uploadAvatar,
    clearAvatar,
  } = useUserProfileStore();

  const [displayName, setDisplayName] = useState('');
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [ordersModalOpen, setOrdersModalOpen] = useState(false);
  const [avatarCropOpen, setAvatarCropOpen] = useState(false);
  const [avatarSourceUrl, setAvatarSourceUrl] = useState<string | null>(null);
  const [avatarSourceName, setAvatarSourceName] = useState('avatar.jpg');
  const [avatarSourceOwner, setAvatarSourceOwner] = useState<string | null>(null);
  const [isPreparingAvatar, setIsPreparingAvatar] = useState(false);
  const avatarPreparationRef = useRef<AbortController | null>(null);
  const [authSecurity, setAuthSecurity] = useState<AuthSecurityData | null>(null);
  const [securityLoading, setSecurityLoading] = useState(false);
  const [securityError, setSecurityError] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscription | null>(null);
  const [activeSection, setActiveSection] = useState<ProfileSettingsSection>('account');
  const avatarContextRef = useRef({ isOpen, userId: user?.id, activeSection });
  avatarContextRef.current = { isOpen, userId: user?.id, activeSection };
  const [largeDobiSpendConfirmEnabled, setLargeDobiSpendConfirmEnabled] = useLargeDobiSpendConfirmEnabled();

  const fetchCurrentSubscription = useCallback(async () => {
    if (!user?.id) {
      setCurrentSubscription(null);
      return;
    }

    try {
      const response = await subscriptionsApi.getCurrent();
      if (response.error) {
        setCurrentSubscription(null);
        return;
      }
      setCurrentSubscription(response.data?.subscription ?? null);
    } catch {
      setCurrentSubscription(null);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isOpen) return;
    fetchProfile(true);
    void fetchCurrentSubscription();
    setActiveSection('account');
  }, [fetchCurrentSubscription, fetchProfile, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setSecurityLoading(true);
    setSecurityError(null);
    fetchAuthSecurity()
      .then((security) => {
        if (!cancelled) {
          setAuthSecurity(security);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setSecurityError(getUserFacingErrorMessage(error, 'auth', authT('securityLoadFailed')));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSecurityLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [authT, fetchAuthSecurity, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const emailPrefix = user?.email?.split('@')[0] || '';
    setDisplayName(profile?.display_name || emailPrefix);
  }, [isOpen, profile?.display_name, user?.email]);

  const userEmail = profile?.email || user?.email || '';
  const fallbackName = userEmail.split('@')[0] || t('defaultUserName');
  const currentName = profile?.display_name || fallbackName;
  const currentTier = resolveCurrentSubscriptionPlanTitle({
    subscription: currentSubscription,
    labels: {
      free: pricingT('tierFree'),
      starter: pricingT('appLightPlanName'),
      basic: pricingT('basicPlanName'),
      pro: pricingT('proPlanName'),
      ultimate: pricingT('ultimatePlanName'),
      enterprise: pricingT('tierEnterprise'),
    },
  });
  const showCurrentTierSkeleton = !currentTier;
  const canSave = displayName.trim().length > 0 && displayName.trim() !== currentName;
  const passwordIsSet = Boolean(authSecurity?.password_set);
  const wechatProvider = authSecurity?.providers.find((provider) => provider.provider === 'wechat') ?? null;
  const showAdminDashboard = Boolean(user?.isAdmin);

  const createdAtLabel = useMemo(() => {
    if (!profile?.created_at) return null;
    const date = new Date(profile.created_at);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }, [locale, profile?.created_at]);

  const closeAvatarCrop = useCallback(() => {
    avatarPreparationRef.current?.abort();
    avatarPreparationRef.current = null;
    setIsPreparingAvatar(false);
    setAvatarCropOpen(false);
    setAvatarSourceUrl(null);
    setAvatarSourceOwner(null);
    setAvatarSourceName('avatar.jpg');
  }, []);

  useEffect(() => {
    if (!isOpen || activeSection !== 'account') closeAvatarCrop();
    return () => {
      avatarPreparationRef.current?.abort();
      avatarPreparationRef.current = null;
    };
  }, [isOpen, user?.id, activeSection, closeAvatarCrop]);

  useEffect(() => {
    // A new account must not inherit the previous account's uncropped source.
    closeAvatarCrop();
  }, [user?.id, closeAvatarCrop]);

  useEffect(() => {
    return () => {
      if (avatarSourceUrl) {
        URL.revokeObjectURL(avatarSourceUrl);
      }
    };
  }, [avatarSourceUrl]);

  const handleSave = async () => {
    const trimmed = displayName.trim();
    if (!trimmed) {
      toast.error(t('displayNameRequired'));
      return;
    }

    const updated = await updateProfile({ display_name: trimmed });
    if (!updated) {
      toast.error(t('profileSaveFailed'));
      return;
    }

    toast.success(t('profileSaved'));
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !isOpen || activeSection !== 'account' || !user?.id || isSaving) return;

    closeAvatarCrop();
    const ownerId = user.id;
    const controller = new AbortController();
    avatarPreparationRef.current = controller;
    setIsPreparingAvatar(true);
    const isCurrent = () => !controller.signal.aborted
      && avatarPreparationRef.current === controller
      && avatarContextRef.current.isOpen
      && avatarContextRef.current.activeSection === 'account'
      && avatarContextRef.current.userId === ownerId
      && useAuthStore.getState().user?.id === ownerId;
    try {
      const response = await userApi.prepareAvatarSource(file, controller.signal);
      if (!isCurrent()) return;
      if (!response.success || !response.data) {
        toast.error(response.error?.message || t('avatarPrepareFailed'));
        return;
      }
      const preparedFile = response.data;
      // Only the decoded local image or the server's JPEG may reach crop img.
      const nextSourceUrl = URL.createObjectURL(preparedFile);
      setAvatarSourceUrl(nextSourceUrl);
      setAvatarSourceName(preparedFile.name || 'avatar.jpg');
      setAvatarSourceOwner(ownerId);
      setAvatarCropOpen(true);
    } catch (error) {
      if (isCurrent()) toast.error(t('avatarPrepareFailed'));
    } finally {
      if (avatarPreparationRef.current === controller) {
        avatarPreparationRef.current = null;
        setIsPreparingAvatar(false);
      }
    }
  };

  const handleAvatarCropConfirm = useCallback(async (file: File, previewUrl: string) => {
    const uploaded = await uploadAvatar(file, previewUrl);
    if (!uploaded) throw new Error('AVATAR_UPLOAD_FAILED');
    closeAvatarCrop();
    toast.success(t('avatarUploaded'));
  }, [closeAvatarCrop, t, uploadAvatar]);

  const handleResetAvatar = async () => {
    const ok = await clearAvatar();
    if (!ok) {
      toast.error(t('avatarResetFailed'));
      return;
    }

    toast.success(t('avatarResetSuccess'));
  };

  const handleLocaleChange = (nextLocale: Locale) => {
    router.replace(selectLocale(nextLocale), { scroll: false });
    router.refresh();
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
    } catch (error) {
      toast.showError(error, 'auth', { fallbackMessage: commonT('error') });
    } finally {
      onClose();
      window.location.replace(POST_LOGOUT_PATH);
    }
  };

  const resetPasswordForm = useCallback(() => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  }, []);

  const settingsNavGroups: Array<{
    label: string;
    items: Array<{
      id: ProfileSettingsSection;
      label: string;
      icon: LucideIcon;
    }>;
  }> = [
    {
      label: t('accountNavGroup'),
      items: [
        { id: 'account', label: t('accountInfo'), icon: UserRound },
        { id: 'general', label: commonT('general'), icon: SlidersHorizontal },
        { id: 'social', label: t('socialAccounts'), icon: Share2 },
        { id: 'billing', label: t('billingSettings'), icon: WalletCards },
        { id: 'security', label: authT('securitySection'), icon: ShieldCheck },
      ],
    },
  ];
  const activeNavItem = settingsNavGroups.flatMap((group) => group.items).find((item) => item.id === activeSection);

  const handlePasswordSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (passwordIsSet && !currentPassword) {
      toast.error(authT('currentPasswordRequired'));
      return;
    }
    if (newPassword.length < 6) {
      toast.error(authT('passwordTooShort'));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(authT('passwordsDoNotMatch'));
      return;
    }

    try {
      setPasswordSaving(true);
      await updatePassword(newPassword, passwordIsSet ? currentPassword : undefined);
      const nextSecurity = await fetchAuthSecurity();
      setAuthSecurity(nextSecurity);
      resetPasswordForm();
      toast.success(passwordIsSet ? authT('passwordChangedSuccess') : authT('passwordSetSuccess'));
    } catch (error) {
      toast.showError(error, 'auth', { fallbackMessage: authT('passwordUpdateFailed') });
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      ariaLabel={t('accountInfo')}
      contentClassName="flex min-h-0 flex-col overflow-hidden !p-0"
      className={`${accountStyles.modal} max-w-[min(80rem,calc(100vw-2rem))] rounded-[10px] border-[#D7DEE8] shadow-[0_24px_70px_rgba(15,23,42,0.24)] [&>div:last-child]:p-0`}
    >
      <LiquidGlass className={accountStyles.modalLens}><span /></LiquidGlass>
      <div className={`${accountStyles.layout} flex flex-col overflow-hidden md:flex-row`}>
        <aside className={`${accountStyles.sidebar} flex shrink-0 flex-col border-b border-[#E5E7EB] bg-[#FBFBFA] px-5 py-5 md:w-[272px] md:border-b-0 md:border-r`}>
          <div className="flex min-w-0 items-center gap-3 pb-5">
            <Avatar
              src={profile?.avatar_url || null}
              userId={profile?.user_id || user?.id || null}
              name={currentName}
              email={userEmail}
              size={44}
              textClassName="text-base"
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[15px] font-semibold leading-5 text-[#2F3135]">{currentName}</p>
              {showCurrentTierSkeleton ? (
                <SensitiveSkeleton className="mt-1 h-4 w-20" />
              ) : (
                <p className="truncate text-[13px] leading-5 text-[#777C85]">{currentTier}</p>
              )}
            </div>
          </div>

          <nav className="min-h-0 flex-1 overflow-y-auto border-t border-[#E7E8EA] pt-4">
            {settingsNavGroups.map((group) => (
              <div key={group.label} className="mb-5">
                <p className="mb-2 px-1 text-[13px] font-semibold leading-5 text-[#8B9098]">{group.label}</p>
                <div className="space-y-1">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const active = item.id === activeSection;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setActiveSection(item.id)}
                        aria-current={active ? 'page' : undefined}
                        className={cn(
                          'flex min-h-10 w-full items-center gap-3 rounded-[10px] px-3 text-left text-[15px] font-semibold transition-colors',
                          active
                            ? 'bg-[#EFEFEE] text-[#25272B]'
                            : 'text-[#4F545C] hover:bg-[#F1F2F3] hover:text-[#25272B]',
                        )}
                      >
                        <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.9} aria-hidden />
                        <span className="min-w-0 flex-1 truncate">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          <button
            type="button"
            onClick={() => {
              onClose();
              router.push('/support');
            }}
            aria-label={t('getHelp')}
            className="mt-4 flex min-h-10 items-center gap-3 border-t border-[#E7E8EA] pt-4 text-left text-[15px] font-semibold text-[#4F545C] transition-colors hover:text-[#25272B]"
          >
            <HelpCircle className="h-[18px] w-[18px] shrink-0" strokeWidth={1.9} aria-hidden />
            <span className="min-w-0 flex-1 truncate">{t('getHelp')}</span>
            <ChevronRight className="h-4 w-4 shrink-0 text-[#8B9098]" strokeWidth={1.9} aria-hidden />
          </button>
        </aside>

        <section className="flex min-h-0 min-w-0 flex-1 flex-col bg-white/35">
          <header className={`${accountStyles.contentHeader} flex shrink-0 items-center justify-between gap-4 border-b border-[#E7E8EA]`}>
            <h2 className="text-[28px] font-semibold leading-9 tracking-normal text-[#25272B]">
              {activeNavItem?.label || t('accountInfo')}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label={commonT('close')}
              className={accountStyles.close}
            >
              <X className="h-5 w-5" strokeWidth={1.9} aria-hidden />
            </button>
          </header>

          <div className={`${accountStyles.contentBody} min-h-0 flex-1 overflow-y-auto`}>
            {activeSection === 'account' ? (
              <div className="max-w-[760px] space-y-8">
                <section className="border-b border-[#ECEDEF] pb-8">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-center gap-4">
                      <Avatar
                        src={profile?.avatar_url || null}
                        userId={profile?.user_id || user?.id || null}
                        name={currentName}
                        email={userEmail}
                        size={60}
                        textClassName="text-xl"
                      />
                      <div className="min-w-0">
                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                          <h3 className="truncate text-[18px] font-semibold leading-6 text-[#25272B]">{currentName}</h3>
                          {showCurrentTierSkeleton ? (
                            <SensitiveSkeleton className="h-[26px] w-20 rounded-full" />
                          ) : (
                            <span className="shrink-0 rounded-full bg-accent-core/10 px-2.5 py-1 text-[12px] font-semibold text-accent-core">
                              {currentTier}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 truncate text-[14px] leading-5 text-[#777C85]">{userEmail}</p>
                        {createdAtLabel ? (
                          <p className="mt-0.5 truncate text-[12px] leading-5 text-[#9AA0A8]">
                            {t('memberSince', { date: createdAtLabel })}
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={IMAGE_FILE_INPUT_ACCEPT}
                        className="hidden"
                        onChange={handleFileChange}
                      />
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={<Upload className="h-4 w-4" />}
                        onClick={() => fileInputRef.current?.click()}
                        isLoading={isSaving || isPreparingAvatar}
                        className={accountStyles.action}
                      >
                        {t(isPreparingAvatar ? 'avatarPreparing' : 'changeAvatar')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<RotateCcw className="h-4 w-4" />}
                        onClick={handleResetAvatar}
                        disabled={!profile?.avatar_url || isSaving || isPreparingAvatar}
                        className={accountStyles.action}
                      >
                        {t('restoreDefaultAvatar')}
                      </Button>
                    </div>
                  </div>
                </section>

                <section>
                  <div className="mb-5 flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-[17px] font-semibold leading-6 text-[#25272B]">{t('profileSection')}</h3>
                      <p className="mt-1 text-[14px] leading-5 text-[#777C85]">{t('profileSectionHint')}</p>
                    </div>
                    <Button
                      onClick={handleSave}
                      disabled={!canSave || isSaving || isLoading}
                      isLoading={isSaving}
                      size="sm"
                      className={accountStyles.action}
                    >
                      {commonT('save')}
                    </Button>
                  </div>

                  <div className="grid gap-5">
                    <div className="grid gap-2">
                      <label htmlFor="profile-name" className="text-[13px] font-semibold text-[#5F6570]">{t('displayName')}</label>
                      <input id="profile-name"
                        value={displayName}
                        onChange={(event) => setDisplayName(event.target.value)}
                        placeholder={t('displayNamePlaceholder')}
                        className="h-12 rounded-[8px] border border-[#DDE3EA] bg-white px-4 text-[15px] text-[#25272B] outline-none transition-colors placeholder:text-[#A0A6AE] focus:border-accent-core/55"
                      />
                    </div>

                    <div className="grid gap-2">
                      <label className="text-[13px] font-semibold text-[#5F6570]">{t('emailAddress')}</label>
                      <div className="flex h-12 items-center rounded-[8px] border border-[#DDE3EA] bg-[#EEF2F6] px-4 text-[15px] text-[#8B9098]">
                        <span className="truncate">{userEmail}</span>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            ) : null}

            {activeSection === 'general' ? (
              <div className="max-w-[760px] space-y-8">
                <section className="border-b border-[#ECEDEF] pb-8">
                  <h3 className="text-[17px] font-semibold leading-6 text-[#25272B]">{t('preferencesSection')}</h3>
                  <p className="mt-1 text-[14px] leading-5 text-[#777C85]">{t('preferencesSectionHint')}</p>
                </section>

                <section>
                  <label className="text-[15px] font-semibold text-[#25272B]">{t('language')}</label>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {routing.locales.map((item) => (
                      <button
                        key={item}
                        type="button"
                        onClick={() => handleLocaleChange(item)}
                        className={cn(
                          'min-h-12 rounded-[8px] border px-4 text-[15px] transition-colors',
                          item === locale
                            ? 'border-accent-core/35 bg-accent-core/10 font-semibold text-accent-core'
                            : 'border-[#DDE3EA] bg-white text-[#5F6570] hover:border-[#CBD3DD] hover:bg-[#F5F6F7]',
                        )}
                      >
                        {localeDefinitions[item].name}
                      </button>
                    ))}
                  </div>
                </section>

                <section className="border-t border-[#ECEDEF] pt-8">
                  <div className="flex items-center justify-between gap-4 rounded-[10px] border border-[#E1E6ED] bg-white px-4 py-3">
                    <div className="min-w-0">
                      <p className="text-[15px] font-semibold text-[#25272B]">{t('largeDobiSpendConfirmPreferenceTitle')}</p>
                      <p className="mt-1 text-[13px] leading-5 text-[#777C85]">{unitText(t('largeDobiSpendConfirmPreferenceHint'))}</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-label={t('largeDobiSpendConfirmPreferenceTitle')}
                      aria-checked={largeDobiSpendConfirmEnabled}
                      onClick={() => setLargeDobiSpendConfirmEnabled(!largeDobiSpendConfirmEnabled)}
                      className="inline-flex h-11 w-14 shrink-0 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-core/30 focus-visible:ring-offset-2"
                    >
                      <span aria-hidden="true" className={cn('relative block h-7 w-12 rounded-full transition-colors', largeDobiSpendConfirmEnabled ? 'bg-accent-core' : 'bg-[#CBD3DD]')}>
                        <span className={cn(
                          'absolute left-1 top-1 h-5 w-5 rounded-full bg-white shadow-[0_2px_8px_rgba(15,23,42,0.18)] transition-transform motion-reduce:transition-none',
                          largeDobiSpendConfirmEnabled ? 'translate-x-5' : 'translate-x-0',
                        )} />
                      </span>
                    </button>
                  </div>
                </section>
              </div>
            ) : null}

            {activeSection === 'social' ? (
              <div className="max-w-[760px]">
                <SocialAccountManager />
              </div>
            ) : null}

            {activeSection === 'billing' ? (
              <div className="max-w-[760px] space-y-8">
                <section className="border-b border-[#ECEDEF] pb-8">
                  <h3 className="text-[17px] font-semibold leading-6 text-[#25272B]">{t('accountActions')}</h3>
                  <p className="mt-1 text-[14px] leading-5 text-[#777C85]">{t('accountActionsHint')}</p>
                  <div className="mt-6 flex items-center justify-between gap-4 rounded-[10px] border border-[#E1E6ED] bg-[#F8F9FA] px-4 py-3">
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold text-[#777C85]">{t('currentPlan')}</p>
                      {showCurrentTierSkeleton ? (
                        <SensitiveSkeleton className="mt-1.5 h-[22px] w-28" />
                      ) : (
                        <p className="mt-0.5 truncate text-[18px] font-semibold text-[#25272B]">{currentTier}</p>
                      )}
                    </div>
                    {showCurrentTierSkeleton ? (
                      <SensitiveSkeleton className="h-[26px] w-20 rounded-full" />
                    ) : (
                      <span className="rounded-full bg-accent-core/10 px-3 py-1 text-[12px] font-semibold text-accent-core">
                        {currentTier}
                      </span>
                    )}
                  </div>
                </section>

                <section className="divide-y divide-[#ECEDEF]">
                  {showTopup ? (
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        pricingModal.open({ triggerReason: 'manual', defaultTab: 'topup' });
                      }}
                      className="flex min-h-14 w-full items-center gap-3 py-3 text-left transition-colors hover:text-accent-core"
                    >
                      <CreditCard className="h-5 w-5 shrink-0 text-[#5F6570]" strokeWidth={1.9} aria-hidden />
                      <span className="min-w-0 flex-1 text-[15px] font-semibold text-[#25272B]">{unitText(t('topUpCredits'))}</span>
                      <ChevronRight className="h-4 w-4 text-[#A0A6AE]" aria-hidden />
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      pricingModal.open({ triggerReason: 'upgrade', defaultTab: 'upgrade' });
                    }}
                    className="flex min-h-14 w-full items-center gap-3 py-3 text-left transition-colors hover:text-accent-core"
                  >
                    <WalletCards className="h-5 w-5 shrink-0 text-[#5F6570]" strokeWidth={1.9} aria-hidden />
                    <span className="min-w-0 flex-1 text-[15px] font-semibold text-[#25272B]">{t('manageSubscription')}</span>
                    <ChevronRight className="h-4 w-4 text-[#A0A6AE]" aria-hidden />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      creatorReferralsModal.open('redeem');
                    }}
                    className="flex min-h-14 w-full items-center gap-3 py-3 text-left transition-colors hover:text-accent-core"
                  >
                    <Gift className="h-5 w-5 shrink-0 text-[#5F6570]" strokeWidth={1.9} aria-hidden />
                    <span className="min-w-0 flex-1 text-[15px] font-semibold text-[#25272B]">{t('redeemOrShareInviteCode')}</span>
                    <ChevronRight className="h-4 w-4 text-[#A0A6AE]" aria-hidden />
                  </button>
                  <button
                    type="button"
                    onClick={() => setOrdersModalOpen(true)}
                    className="flex min-h-14 w-full items-center gap-3 py-3 text-left transition-colors hover:text-accent-core"
                  >
                    <FileText className="h-5 w-5 shrink-0 text-[#5F6570]" strokeWidth={1.9} aria-hidden />
                    <span className="min-w-0 flex-1 text-[15px] font-semibold text-[#25272B]">{t('ordersAndInvoices')}</span>
                    <ChevronRight className="h-4 w-4 text-[#A0A6AE]" aria-hidden />
                  </button>
                  {showAdminDashboard ? (
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        router.push('/admin');
                      }}
                      className="flex min-h-14 w-full items-center gap-3 py-3 text-left transition-colors hover:text-accent-core"
                    >
                      <LayoutDashboard className="h-5 w-5 shrink-0 text-[#5F6570]" strokeWidth={1.9} aria-hidden />
                      <span className="min-w-0 flex-1 text-[15px] font-semibold text-[#25272B]">{t('openAdminDashboard')}</span>
                      <ChevronRight className="h-4 w-4 text-[#A0A6AE]" aria-hidden />
                    </button>
                  ) : null}
                </section>
              </div>
            ) : null}

            {activeSection === 'security' ? (
              <div className="max-w-[760px] space-y-8">
                <section>
                  <h3 className="text-[17px] font-semibold leading-6 text-[#25272B]">{authT('securitySection')}</h3>
                  <p className="mt-1 text-[14px] leading-5 text-[#777C85]">{authT('securitySectionHint')}</p>
                </section>

                {securityLoading ? (
                  <div className="flex h-24 items-center justify-center rounded-[10px] border border-[#E1E6ED] bg-[#F8F9FA] text-[14px] text-[#777C85]">
                    {authT('securityLoading')}
                  </div>
                ) : authSecurity ? (
                  <>
                    <section className="space-y-1">
                      <div className="flex items-center justify-between gap-4 py-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <CheckCircle2 className="h-5 w-5 shrink-0 text-accent-core" strokeWidth={1.9} aria-hidden />
                          <div className="min-w-0">
                            <p className="text-[15px] font-semibold text-[#25272B]">{authT('primaryEmail')}</p>
                            <p className="truncate text-[13px] text-[#777C85]">{authSecurity.email}</p>
                          </div>
                        </div>
                        <span className="shrink-0 rounded-full bg-accent-core/10 px-3 py-1 text-[12px] font-semibold text-accent-core">
                          {authSecurity.email_verified ? authT('emailVerified') : authT('emailNotVerified')}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-4 py-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <Link2 className="h-5 w-5 shrink-0 text-[#5F6570]" strokeWidth={1.9} aria-hidden />
                          <p className="text-[15px] font-semibold text-[#25272B]">{authT('wechatBinding')}</p>
                        </div>
                        <span className="min-w-0 truncate text-right text-[14px] text-[#777C85]">
                          {wechatProvider?.metadata?.nickname || (wechatProvider ? authT('wechatBound') : authT('wechatNotBound'))}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-4 py-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <ShieldCheck className="h-5 w-5 shrink-0 text-[#5F6570]" strokeWidth={1.9} aria-hidden />
                          <p className="text-[15px] font-semibold text-[#25272B]">{authT('passwordLogin')}</p>
                        </div>
                        <span className="shrink-0 text-[14px] text-[#777C85]">
                          {passwordIsSet ? authT('passwordSet') : authT('passwordNotSet')}
                        </span>
                      </div>
                    </section>

                    <form onSubmit={handlePasswordSubmit} className="pt-2">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-accent-core/10 text-accent-core">
                          <KeyRound className="h-[18px] w-[18px]" strokeWidth={1.9} aria-hidden />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-[17px] font-semibold leading-6 text-[#25272B]">
                            {passwordIsSet ? authT('changePasswordTitle') : authT('setPasswordTitle')}
                          </h3>
                          <p className="mt-1 text-[14px] leading-5 text-[#777C85]">
                            {passwordIsSet ? authT('changePasswordSubtitle') : authT('setPasswordSubtitle')}
                          </p>
                        </div>
                      </div>

                      <div className="mt-5 grid gap-5">
                        {passwordIsSet ? (
                          <div className="grid gap-2">
                            <label htmlFor="profile-current-password" className="text-[13px] font-semibold text-[#5F6570]">{authT('currentPassword')}</label>
                            <input id="profile-current-password"
                              type="password"
                              autoComplete="current-password"
                              value={currentPassword}
                              onChange={(event) => setCurrentPassword(event.target.value)}
                              placeholder={authT('enterCurrentPassword')}
                              className="h-12 rounded-[8px] border border-[#DDE3EA] bg-white px-4 text-[15px] text-[#25272B] outline-none transition-colors placeholder:text-[#A0A6AE] focus:border-accent-core/55"
                            />
                          </div>
                        ) : null}

                        <div className="grid gap-5 sm:grid-cols-2">
                          <div className="grid gap-2">
                            <label htmlFor="profile-new-password" className="text-[13px] font-semibold text-[#5F6570]">{authT('newPassword')}</label>
                            <input id="profile-new-password"
                              type="password"
                              autoComplete="new-password"
                              value={newPassword}
                              onChange={(event) => setNewPassword(event.target.value)}
                              placeholder={authT('passwordPlaceholder')}
                              className="h-12 rounded-[8px] border border-[#DDE3EA] bg-white px-4 text-[15px] text-[#25272B] outline-none transition-colors placeholder:text-[#A0A6AE] focus:border-accent-core/55"
                            />
                          </div>
                          <div className="grid gap-2">
                            <label htmlFor="profile-confirm-password" className="text-[13px] font-semibold text-[#5F6570]">{authT('confirmNewPassword')}</label>
                            <input id="profile-confirm-password"
                              type="password"
                              autoComplete="new-password"
                              value={confirmPassword}
                              onChange={(event) => setConfirmPassword(event.target.value)}
                              placeholder={authT('reEnterNewPassword')}
                              className="h-12 rounded-[8px] border border-[#DDE3EA] bg-white px-4 text-[15px] text-[#25272B] outline-none transition-colors placeholder:text-[#A0A6AE] focus:border-accent-core/55"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="mt-5 flex justify-end">
                        <Button type="submit" size="sm" icon={<KeyRound className="h-4 w-4" />} isLoading={passwordSaving} disabled={securityLoading} className={accountStyles.action}>
                          {passwordIsSet ? authT('changePasswordAction') : authT('setPasswordAction')}
                        </Button>
                      </div>
                    </form>
                  </>
                ) : (
                  <div className="rounded-[10px] border border-semantic-error/20 bg-semantic-error-bg px-4 py-3 text-[14px] text-semantic-error">
                    {securityError || authT('securityLoadFailed')}
                  </div>
                )}

                <section className="border-t border-[#ECEDEF] pt-6">
                  <button
                    type="button"
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className={cn(
                      'flex min-h-12 w-full items-center gap-3 rounded-[12px] px-3 text-left text-[15px] font-semibold transition-colors',
                      isLoggingOut
                        ? 'cursor-wait bg-accent-core/6 text-accent-core'
                        : 'text-semantic-error hover:bg-semantic-error-bg',
                    )}
                  >
                    <LogOut className="h-5 w-5 shrink-0" strokeWidth={1.9} aria-hidden />
                    <span className="min-w-0 flex-1 truncate">{isLoggingOut ? t('signingOut') : t('signOut')}</span>
                  </button>
                </section>
              </div>
            ) : null}
          </div>
        </section>
      </div>

      <AvatarCropModal
        isOpen={avatarCropOpen && isOpen && activeSection === 'account' && avatarSourceOwner === user?.id}
        imageUrl={avatarSourceUrl}
        fileName={avatarSourceName}
        userId={profile?.user_id || user?.id || null}
        displayName={currentName}
        email={userEmail}
        onClose={closeAvatarCrop}
        onConfirm={handleAvatarCropConfirm}
      />

      <OrdersInvoicesModal
        isOpen={ordersModalOpen}
        onClose={() => setOrdersModalOpen(false)}
        defaultEmail={userEmail}
      />
    </Modal>
  );
}
